import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { ArrowRight } from 'lucide-react';
import Button from '../../Button';
import Hyperlink from '../../Hyperlink';
import '../../shared/Card.css';
import './BentoGrid.css';

/**
 * BentoGrid — "Patch Universe": a title block with filter tabs, a dense
 * mosaic of patch cards (small / wide / one photo feature card) and a CTA.
 *
 * Figma (OVER-LL): Desktop `2222:152661` (6 columns, 248px rows), Mobile
 * `2645:13450` (2 columns, 164px rows).
 *
 * - Tabs are DS tertiary Buttons (white = active, gray = rest). The first tab
 *   means "all"; any other shows the items whose `tags` include it. Items
 *   without `tags` (the feature card) always stay.
 * - `grid-auto-flow: dense` packs the spans the way the Figma frames do, so
 *   the consumer passes the items in desktop reading order.
 * - Cards reuse the DS card surface (`.ds-card`).
 */
const BentoGrid = ({
  caption = '',
  title = '',
  tabs = [],
  defaultTab,
  activeTab: activeTabProp,
  onTabChange,
  items = [],
  ctaLabel = '',
  ctaVariant = 'outline',
  ctaHref,
  ctaOnClick,
  className = '',
  ...rest
}) => {
  const [internalTab, setInternalTab] = useState(defaultTab ?? tabs[0]?.id);
  const activeTab = activeTabProp ?? internalTab;
  const allId = tabs[0]?.id;

  const selectTab = (id) => {
    if (activeTabProp === undefined) setInternalTab(id);
    onTabChange?.(id);
  };

  const visible = !activeTab || activeTab === allId
    ? items
    : items.filter((item) => !item.tags || item.tags.includes(activeTab));

  const rootClass = ['ds-bento', className].filter(Boolean).join(' ');

  return (
    <section className={rootClass} {...rest}>
      <div className="ds-bento__container">
        {(caption || title || tabs.length > 0) && (
          <div className="ds-bento__top">
            {(caption || title) && (
              <div className="ds-bento__heading">
                {caption && <p className="ds-bento__caption typo-body">{caption}</p>}
                {title && <h2 className="ds-bento__title typo-h2-editorial">{title}</h2>}
              </div>
            )}
            {tabs.length > 0 && (
              <div className="ds-bento__tabs" role="tablist">
                {tabs.map(({ id, label }) => (
                  <Button
                    key={id}
                    size="tertiary"
                    variant={id === activeTab ? 'white' : 'gray'}
                    role="tab"
                    aria-selected={id === activeTab}
                    onClick={() => selectTab(id)}
                  >
                    {label}
                  </Button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="ds-bento__grid">
          {visible.map((item) => {
            const size = item.size || 'small';
            const interactive = Boolean(item.onClick);
            const itemClass = [
              'ds-card',
              'ds-bento__item',
              `ds-bento__item--${size}`,
              interactive && 'ds-card--interactive',
            ].filter(Boolean).join(' ');

            if (size === 'feature') {
              const handleLink = (e) => {
                if (!item.href) e.preventDefault();
                item.onClick?.(e);
              };
              return (
                <article key={item.id} className={itemClass}>
                  <div className="ds-bento__feature-media">
                    <img className="ds-bento__feature-img" src={item.image} alt={item.alt || ''} loading="lazy" />
                    <div className="ds-bento__feature-body">
                      {item.title && <h3 className="ds-bento__feature-title typo-h2">{item.title}</h3>}
                      {item.linkLabel && (
                        <Hyperlink
                          variant="large"
                          href={item.href || '#'}
                          onClick={handleLink}
                          iconRight={<ArrowRight size={20} />}
                          showIconRight
                          className="ds-bento__feature-link"
                        >
                          {item.linkLabel}
                        </Hyperlink>
                      )}
                    </div>
                  </div>
                </article>
              );
            }

            return (
              <figure key={item.id} className={itemClass}>
                <img className="ds-bento__img" src={item.image} alt={item.alt || ''} loading="lazy" />
                {interactive && (
                  <button
                    type="button"
                    className="ds-card__hit"
                    aria-label={item.alt || item.title}
                    onClick={item.onClick}
                  />
                )}
              </figure>
            );
          })}
        </div>

        {ctaLabel && (
          <div className="ds-bento__cta">
            <Button size="secondary" variant={ctaVariant} href={ctaHref} onClick={ctaOnClick}>
              {ctaLabel}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

BentoGrid.propTypes = {
  /** Eyebrow above the title (uppercase, gray-4) */
  caption: PropTypes.string,
  /** Section title (typo-h2-editorial) */
  title: PropTypes.string,
  /** Filter tabs; the first one shows everything */
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
  /** Initially active tab id (uncontrolled); defaults to the first tab */
  defaultTab: PropTypes.string,
  /** Active tab id (controlled) */
  activeTab: PropTypes.string,
  /** Called with the tab id on every tab click */
  onTabChange: PropTypes.func,
  /**
   * Cards in desktop reading order. `size`: 'small' (1×1, default), 'wide'
   * (2×1) or 'feature' (2×2 photo card with `title` + `linkLabel`/`href`).
   * `tags` = tab ids the card belongs to; no tags = always shown.
   */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      image: PropTypes.string.isRequired,
      alt: PropTypes.string,
      size: PropTypes.oneOf(['small', 'wide', 'feature']),
      tags: PropTypes.arrayOf(PropTypes.string),
      title: PropTypes.string,
      linkLabel: PropTypes.string,
      href: PropTypes.string,
      onClick: PropTypes.func,
    }),
  ),
  /** CTA label under the grid (Button secondary / outline) */
  ctaLabel: PropTypes.string,
  /** CTA handler */
  /** CTA Button variant (outline by default) */
  ctaVariant: PropTypes.string,
  /** CTA link target — renders the CTA as a real link */
  ctaHref: PropTypes.string,
  ctaOnClick: PropTypes.func,
  /** Additional CSS class */
  className: PropTypes.string,
};

export default BentoGrid;
